import { useEffect, useState, useMemo, useCallback } from "react";
import CustomCard from "../../components/CustomCard/CustomCard"
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import { getStatusList, getEnquiryManagementView } from "../../redux/slices/EnqMngtSlice";
import EnqMngtTable from "./Components/EnqMngtTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers"
import { TITLE_TXT, BTN_TXT, ENQ_STATUS_DISPLAY_NAMES, STATUS } from "../../constants/constants";
import { ROLE_MODAL_WIDTHS } from "../../constants/constants";
import CustomModal from "../../components/CustomModal/CustomModal";
import SuccessConfirmationModal from "../../components/SuccessConfirmationModal/SuccessConfirmationModal";
import "./EnqMngt.scss"
import NewEnqForm from "./Components/NewEnqForm";
import { PERM_CODE, ENQ_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";
import { count } from "../../redux/slices/dealsSlice";

const KYC_STATUS_GROUP = [
  STATUS.KYC_PENDING.ID,
  STATUS.KYC_VERIFIED.ID,
  STATUS.KYC_REVIEW.ID,
];

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const EnqMngt = () => {
  const dispatch = useDispatch();
  const { statusList, enquiryList } = useSelector((state) => state?.enqMngt);
  const { CountData } = useSelector((state) => state?.deals);
  const docLoading = useDocDownloadError();
  const [columnControl, setColumnControl] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [modal, setModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const { userPrivileges, allowedStatusIds } = useUserPrivileges(ENQ_STATUS_PRIVILEGE_MAP);

const DEFAULT_ENQ_STATUSES = [
  STATUS.PENDING.ID,
  STATUS.KYC_PENDING.ID,
  STATUS.ACTIVE.ID,
];

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("bus"));
      if (res.payload?.statusCode === 200) {
        const preferredStatuses = DEFAULT_ENQ_STATUSES.filter((id) =>
          allowedStatusIds.includes(id),
        );
        setSelectedStatus(
          preferredStatuses.length > 0
            ? preferredStatuses
            : allowedStatusIds[0]
            ? [allowedStatusIds[0]]
            : [],
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  useEffect(() => {
    if (selectedStatus) {
      dispatch(getEnquiryManagementView(selectedStatus));
    }
  }, [dispatch, selectedStatus]);

  useEffect(() => {
    dispatch(count("ENQUIRYMGMT"));
  }, [dispatch]);

  const totalKycCount = useMemo(
    () => CountData
      ?.filter((c) => KYC_STATUS_GROUP.includes(c.statusId))
      ?.reduce((sum, item) => sum + parseFloat(item.count), 0) || 0,
    [CountData],
  );

  const { searchText, setSearchText, filteredData } = useSearchableTable(enquiryList);

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);

  const filteredStatusList = useMemo(
    () => statusList?.filter((status) => allowedStatusIds.includes(status.statusId)) ?? [],
    [statusList, allowedStatusIds],
  );

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      if (item.statusId === STATUS.KYC_PENDING.ID) {
        return { ...item, count: totalKycCount };
      }
      return {
        ...item,
        count: CountData?.find((c) => c.statusId === item.statusId)?.count || 0,
      };
    }),
    [filteredStatusList, totalKycCount, CountData],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({ dispatch, type, screenName: "enquiryMgmt", statusId: selectedStatus }),
    [dispatch, selectedStatus],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);
  const handleOpenModal = useCallback(() => setModal(true), []);
  const handleCloseModal = useCallback(() => setModal(false), []);
  const handleCloseSuccessModal = useCallback(() => setSuccessModal(false), []);

  const handleFormSuccess = useCallback(() => {
    setModal(false);
    setSuccessModal(true);
    setSelectedStatus([STATUS.PENDING.ID]);
  }, []);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: ENQ_STATUS_DISPLAY_NAMES,
        statusListWithCounts,
        defaultTitle: "Enquiry",
      }),
    [selectedStatus, statusListWithCounts],
  );

  const addButtonLabel = useMemo(
    () => (userPrivileges.includes(PERM_CODE.ECACC) ? BTN_TXT.ADD_NEW_ENQ : undefined),
    [userPrivileges],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={setSearchText}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={ENQ_STATUS_DISPLAY_NAMES}
        btnName={addButtonLabel}
        handleBtnOnClick={handleOpenModal}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
      />
      <EnqMngtTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        onColumnControlChange={setColumnControl}
      />
      {modal && (
        <CustomModal
          title={TITLE_TXT.ADD_NEW_ENQ}
          open
          centered
          closeIcon
          onCancel={handleCloseModal}
          width={ROLE_MODAL_WIDTHS}
          content={
            <NewEnqForm
              onClose={handleCloseModal}
              selectedStatus={selectedStatus}
              onSuccess={handleFormSuccess}
            />
          }
        />
      )}
      <SuccessConfirmationModal
        open={successModal}
        onClose={handleCloseSuccessModal}
        title="New enquiry added successfully"
        description="Your login details have been sent to your registered email. Please log in to continue."
      />
    </CustomCard>
  )
}

export default EnqMngt